import { useState, ChangeEvent } from 'react';
import DOMPurify from 'dompurify';
import Button from './buttons/baseButton';
import PostUploadCampaignSmsTemplate from '../services/adapters/uploadCampaignSmsTemplate';
import useRequestState from '../hooks/useRequestState';
import { gsmCharset } from '../utils/constants';
import SMS from './campaign-confirmation/smsPreview';

interface UploadSmsTemplateProps {
  readonly parentText: string;
  readonly smsSenderId: string;
  readonly onSubmitSuccess?: (text: string, smsTemplateId: number) => void;
}

interface SmsTemplateResponse {
  templateText: string;
  smsTemplateId: number;
}

export default function UploadSmsTemplate({
  parentText,
  smsSenderId,
  onSubmitSuccess,
}: UploadSmsTemplateProps) {
  const [text, setText] = useState(parentText);
  const [validationError, setValidationError] = useState<string | null>(null);

  const {
    loading, error, handleRequest,
  } = useRequestState<SmsTemplateResponse>();

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    setValidationError(null);
  };

  const getInvalidGsmChars = (input: string) => [...input].filter((char) => !gsmCharset.has(char));

  const handleSubmit = async () => {
    const trimmedText = text.trim();
    if (!trimmedText) return;

    const sanitizedText = DOMPurify.sanitize(trimmedText);
    const invalidChars = getInvalidGsmChars(sanitizedText);

    if (invalidChars.length > 0) {
      setValidationError(
        `SMS template contains invalid characters: ${[...new Set(invalidChars)].join(', ')}`,
      );
      return;
    }

    setValidationError(null);

    const adapter = new PostUploadCampaignSmsTemplate();
    const response = await handleRequest(adapter.adaptResponse({ template: sanitizedText }));

    if (response) {
      setText(response.templateText);
      onSubmitSuccess?.(response.templateText, response.smsTemplateId);
    }
  };

  const combinedError = validationError || (error ? error.message : null);

  return (
    <div className="flex flex-col gap-2 items-center w-full">
      <textarea
        value={text}
        onChange={handleChange}
        disabled={loading}
        className="p-2 rounded-md border border-pipgrey-light w-full"
        placeholder="Enter your SMS template text..."
      />

      <Button
        id="sms-upload-button"
        className="button-green w-3/4"
        onClick={handleSubmit}
        label={loading ? 'Saving...' : 'Save Template'}
        isDisabled={loading || !text.trim()}
      />

      {loading && <span className="text-sm text-secondary">Uploading SMS template…</span>}
      {combinedError && <span className="text-sm text-piptext-error">{combinedError}</span>}
      {parentText && <span className="text-sm text-primary">SMS template saved successfully and attached to Campaign</span>}

      <SMS sender={smsSenderId} message={text} showFallback={false} />
    </div>
  );
}
